/**
 * Text and labels for the KYC VARA screens. Codes match the backend (src/kyc_vara/constants.js).
 * The classification itself always comes from the server; nothing here decides it.
 */

export const ACCOUNT_TYPES = [
  {
    value: "INDIVIDUAL",
    title: "Individual",
    subtitle: "Trade with your personal funds",
    icon: "ri-user-3-line",
    note: "The questions and documents you see next depend on the account type you choose.",
  },
  {
    value: "CORPORATE",
    title: "Corporate",
    subtitle: "Company, fund, VASP or other legal entity",
    icon: "ri-building-4-line",
    note: "Corporate accounts need company documents and verification of every Ultimate Beneficial Owner.",
  },
];

export const QUESTIONS = {
  income: {
    field: "income_gte_700k",
    eyebrow: "Question 1 of 2",
    INDIVIDUAL: {
      title: "Is your annual income equal to or greater than AED 700,000?",
      hint: "This helps us set your investor classification.",
    },
    CORPORATE: {
      title: "Is your company's annual income equal to or greater than AED 700,000?",
      hint: "This helps us set your company's classification.",
    },
    yes: "AED 700,000 or more per year",
    no: "Less than AED 700,000 per year",
  },
  assets: {
    field: "assets_gte_3_5m",
    eyebrow: "Question 2 of 2",
    INDIVIDUAL: {
      title: "Do you hold assets worth AED 3.5 million or more?",
      hint: "Consider the total value of the assets you own.",
      note: "Answering Yes to either question classifies you as a Qualified Individual Retailer.",
    },
    CORPORATE: {
      title: "Does your company hold assets worth AED 3.5 million or more?",
      hint: "Consider the total value of assets held by the company.",
      note: "If you answer No to either question, your company is classified as a Corporate Retailer.",
    },
    yes: "AED 3.5 million or more",
    no: "Less than AED 3.5 million",
  },
};

export const BUSINESS_TYPES = [
  { value: "VASP", title: "Virtual Asset Service Provider (VASP)", short: "VASP", icon: "ri-coin-line" },
  { value: "REGULATED_FI", title: "Regulated financial institution or regulated fintech", short: "Regulated financial institution", icon: "ri-shield-check-line" },
  { value: "GOVERNMENT", title: "Government entity with virtual-asset activity", short: "Government entity", icon: "ri-government-line" },
  { value: "BANK_FS", title: "Bank, central-bank-related institution or financial services entity", short: "Bank / financial services", icon: "ri-bank-line" },
  { value: "MARKET_MAKER", title: "Market maker, proprietary trading desk or trading desk", short: "Market maker / trading desk", icon: "ri-line-chart-line" },
  { value: "OTHER", title: "Other entity", short: "Other entity", icon: "ri-briefcase-line" },
];

export function businessTypeLabel(value) {
  return BUSINESS_TYPES.find((type) => type.value === value)?.short || "";
}

export const CLASSIFICATIONS = {
  INDIVIDUAL_RETAILER: {
    label: "Individual Retailer",
    reason: () => "You answered No to both eligibility questions.",
  },
  QUALIFIED_INDIVIDUAL_RETAILER: {
    label: "Qualified Individual Retailer",
    reason: () =>
      "You answered Yes to at least one eligibility question (income ≥ AED 700,000 or assets ≥ AED 3.5M).",
  },
  CORPORATE_RETAILER: {
    label: "Corporate Retailer",
    reason: () => "Your company answered No to the income or assets question.",
  },
  QUALIFIED_CORPORATE: {
    label: "Qualified Corporate",
    reason: () => "Both financial criteria are met and your business type is Other entity.",
  },
  INSTITUTIONAL: {
    label: "Institutional",
    reason: (businessType) =>
      `Both financial criteria are met and your business type is ${businessTypeLabel(businessType)}.`,
  },
};

/** Shown on the corporate result screen ("How corporate classification works"). */
export const CORPORATE_CLASSES = [
  { value: "CORPORATE_RETAILER", rule: "No to income or assets" },
  { value: "QUALIFIED_CORPORATE", rule: "Yes to both · Other entity" },
  { value: "INSTITUTIONAL", rule: "Yes to both · VASP, regulated FI, government, bank or trading desk" },
];

export const DOCUMENT_CATEGORIES = [
  { code: "A", title: "Company and legal documents" },
  { code: "B", title: "Ownership and control" },
  { code: "C", title: "KYC of relevant persons" },
  { code: "D", title: "Authority to open an account" },
  { code: "E", title: "Financial evidence" },
  { code: "F", title: "Regulatory evidence" },
  { code: "G", title: "Virtual-asset compliance" },
  { code: "H", title: "Business and account profile" },
];

/** What Didit will ask an individual for (shown before starting). */
export const INDIVIDUAL_STEPS = [
  { title: "ID document", subtitle: "Emirates ID, passport or national ID", icon: "ri-id-card-line" },
  { title: "Liveness check", subtitle: "A short selfie video", icon: "ri-user-smile-line" },
  { title: "AML screening", subtitle: "Runs automatically after your ID", icon: "ri-search-line" },
];

/** Rows on the status screen, keyed like the backend `checks[].key`. */
export const CHECKS = {
  id_document: { title: "ID document", subtitle: "Emirates ID, passport or national ID", icon: "ri-id-card-line" },
  liveness: { title: "Liveness check", subtitle: "Selfie video", icon: "ri-user-smile-line" },
  aml: { title: "AML screening", subtitle: "Sanctions & PEP lists", icon: "ri-search-line" },
  company_registry: { title: "Company registry", subtitle: "Registration and status", icon: "ri-building-4-line" },
  company_documents: { title: "Company documents", subtitle: "Required document categories", icon: "ri-file-list-3-line" },
  beneficial_owners: { title: "Beneficial owners", subtitle: "ID, liveness and AML for every UBO", icon: "ri-team-line" },
};

export const CHECK_STATUS = {
  APPROVED: { label: "Verified", tone: "success" },
  DECLINED: { label: "Declined", tone: "danger" },
  IN_REVIEW: { label: "In review", tone: "warning" },
  PENDING: { label: "Pending", tone: "muted" },
};
